import React, { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useWorkspacePicker } from 'workspaces/api';
import { page_size } from 'shared/api/apiUtils';
import { Organization } from 'shared/types/ObjectTypes';
import NotFoundPage from 'shared/components/layout/NotFoundPage';
import UnsavedSectionPrompt, {
  PendingEdits,
  SectionPrompt,
} from 'shared/components/configPanel/UnsavedSectionPrompt';
import { useKeycloakToken } from 'shared/auth/useKeycloak';
import { isValidRouteSubpage } from 'routes/routeSubpages';
import ConnectionDetailsModal from './ConnectionDetailsModal';
import AddToRouteDrawer from './AddToRouteDrawer';
import {
  closeTarget,
  connectionSubPage,
} from './connectionDetails/connectionPath';
import { useConnectionRecord } from './connectionDetails/useConnectionRecord';
import { useConnectionRoutes } from './connectionDetails/useConnectionRoutes';
import { useAddToRoute } from './connectionDetails/useAddToRoute';
import { useConnectionTestAction } from './connectionDetails/useConnectionTestAction';
import UseAsDataSourceModal from './connectionDetails/UseAsDataSourceModal';

// The Connection screen: its Configuration and the Routes it takes part in,
// plus the ways to put it to work in a Route.
export function ManageConnectionDetails() {
  const { keycloak } = useKeycloak();
  const [token] = useKeycloakToken();
  const { dest_id, subpage } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentSubPage = connectionSubPage(location.pathname, dest_id);
  // An invalid :subpage renders the 404 page, so nothing is fetched for it.
  const isLoggedIn =
    !!keycloak?.authenticated &&
    localStorage.getItem('userAuthorized') !== null;
  const canFetch =
    isValidRouteSubpage(subpage) &&
    isLoggedIn &&
    !!dest_id &&
    !!keycloak.token &&
    !keycloak.isTokenExpired();

  const record = useConnectionRecord({ connectionId: dest_id, canFetch });
  const { connection, connectionLoaded } = record;
  const routes = useConnectionRoutes({
    connectionId: dest_id,
    canFetch,
    hasRecord: record.hasRecord,
  });

  // What the configuration panel currently has unsaved. The tabs and the links
  // out of this page are here, not in the panel, so they ask it before moving.
  const pendingEditsRef = useRef<PendingEdits | null>(null);
  const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(
    null,
  );
  // Any navigation that would unmount the configuration panel and take its
  // unsaved edits with it. Routed through the same Save-or-discard question the
  // panel asks when you switch sections inside it.
  const guardedGo = (go: () => void) => {
    const pending = pendingEditsRef.current;
    if (!pending?.hasEdits()) return go();
    setSectionPrompt({
      onSave: () => pending.save(),
      onDiscard: () => pending.discard(),
      proceed: go,
    });
  };

  const [confirmDataSource, setConfirmDataSource] = useState(false);
  const [creatingDataSource, setCreatingDataSource] = useState(false);
  const testAction = useConnectionTestAction(connection, token);
  const scrollableContentRef = useRef<HTMLDivElement | null>(null);

  // Closing the configuration screen unmounts the panel, so it asks first for
  // the same reason a tab switch does.
  const onToggleModal = () =>
    guardedGo(() => {
      const target = closeTarget(location.pathname);
      if (target) navigate(target);
    });

  const { organizations, searchWorkspaces } = useWorkspacePicker({
    enabled: canFetch,
  });
  const addToRoute = useAddToRoute({ connection, connectionId: dest_id });

  // Capability decides which roles are on offer: pull to be a source, push to be
  // a destination. A pull-only technology cannot receive data, so attaching it to a
  // Route would produce a destination that can never work.
  const canBeDataSource =
    connectionLoaded &&
    (connection.type?.actions || []).some((a: any) => a.type === 'pull');
  const canBeDestination =
    connectionLoaded &&
    (connection.type?.actions || []).some((a: any) => a.type === 'push');

  function handleSelectSubPage(e: string) {
    if (!dest_id) return;
    guardedGo(() => {
      // A Connection has two addresses: the bare path IS Configuration,
      // and /routes is the Routes tab. Navigating to /configuration here
      // would land on the redirect component instead of this screen, so
      // React would tear the whole page down and rebuild it — every load
      // effect re-running — for what should be a tab swap.
      navigate(
        e === 'details'
          ? `/connections/${dest_id}/routes`
          : `/connections/${dest_id}`,
      );
    });
  }

  // The /routes/:route_id/:subpage/:dest_id(/configuration) routes
  // accept any :subpage string — validate it like ManageRouteDetails does
  if (!isValidRouteSubpage(subpage)) return <NotFoundPage />;
  // Routes and Connections now address the same id space — the provider
  // INTEGRATION id — so an id that is not an integration is not a route
  // record's stand-in either. There is nothing left to probe: a missing
  // integration is a 404 wherever this screen is mounted. (Links minted with
  // the old route-record id are deliberately not redirected.)
  if (record.notFound) return <NotFoundPage />;

  // /connections/:id used to bounce to /routes/:id whenever the record fed a
  // Route — a GUNDI-5639 measure for links minted when "connection" meant what
  // is now a Route. It is gone: a Connection has exactly two addresses,
  // /connections/:id (Configuration) and /connections/:id/routes, and the bare
  // one has to actually show the Connection or it means nothing. A record that
  // feeds Routes still lists them one click away, on its Routes tab.

  return (
    <>
      <ConnectionDetailsModal
        currentMemberIsAdmin={record.currentMemberIsAdmin}
        connection={connection}
        connectionTitle={record.connectionTitle}
        destSubPage={currentSubPage}
        pendingEditsRef={pendingEditsRef}
        onSelectSubPage={handleSelectSubPage}
        organizations={organizations}
        loadMoreOrgs={() => {}}
        orgInputChange={(e: any) => searchWorkspaces(e)}
        routes={routes.receivingRoutes}
        onToggleModal={onToggleModal}
        requestSort={routes.requestSort}
        loadingRoutes={routes.loadingRoutes}
        onChangeSelectedConnection={record.onChangeField}
        onChangeSelectedConnectionOwner={record.onChangeOwner}
        onTestAction={testAction.run}
        testActionLoading={testAction.loading}
        testActionResponse={testAction.response}
        onSave={record.save}
        onReloadConnection={record.discardAndReload}
        onReloadConnectionAndWait={record.reloadAndWait}
        providerRoutes={routes.providerRoutes}
        providerRoleFailed={routes.providerRoleFailed}
        destRoutesFailed={routes.destRoutesFailed}
        canBeDataSource={canBeDataSource}
        canBeDestination={canBeDestination}
        // Deliberately NOT && connectionLoaded: this flag drives the
        // skeleton/error/table display for role data that is keyed on its
        // own, so a stalled or failed details fetch must not hide the
        // "Couldn't load" message behind an eternal skeleton. The role
        // ACTIONS are gated separately via canBeDataSource/canBeDestination.
        providerRoleLoaded={routes.providerRoleLoaded}
        onUseAsDataSource={() => setConfirmDataSource(true)}
        onAddToRoute={addToRoute.open}
        changesMade={record.changesMade}
        errors={record.errors}
        onPageClicked={routes.onPageClicked}
        totalCount={routes.totalCount}
        pageLoaded={routes.pageLoaded}
        take={page_size}
        offset={routes.offset}
        scrollableContentRef={scrollableContentRef}
        authToken={
          token && typeof token === 'string' && !keycloak.isTokenExpired()
            ? token
            : undefined
        }
      />
      <UnsavedSectionPrompt
        prompt={sectionPrompt}
        onClose={() => setSectionPrompt(null)}
      />
      <AddToRouteDrawer
        isOpen={addToRoute.isOpen}
        onClose={addToRoute.close}
        candidates={addToRoute.candidates}
        loading={addToRoute.loading}
        onAttach={addToRoute.attach}
        onSearch={(searchStr: string) => addToRoute.search(searchStr)}
        hasMore={addToRoute.hasMore}
        workspaceOptions={organizations
          .filter((o: Organization) => o.id)
          .map((o: Organization) => ({ label: o.name, value: o.id as string }))}
        workspaceFilter={addToRoute.workspaceFilter}
        onChangeWorkspaceFilter={(option) =>
          addToRoute.setWorkspaceFilter(option)
        }
      />
      {confirmDataSource && (
        <UseAsDataSourceModal
          connection={connection}
          connectionLoaded={connectionLoaded}
          creating={creatingDataSource}
          setCreating={setCreatingDataSource}
          onClose={() => setConfirmDataSource(false)}
        />
      )}
    </>
  );
}
